import { clone, isMessage } from "@bufbuild/protobuf"
import { useCallback, useEffect, useRef, useState } from "react"
import { useSearchParams } from 'react-router'
import type { Enrollment, Group, Submission } from "../../proto/qf/types_pb"
import { EnrollmentSchema } from "../../proto/qf/types_pb"
import { ScreenSize } from "../consts"
import { Color, convertToBigInt } from "../Helpers"
import { useCourseID } from "../hooks/useCourseID"
import useWindowSize from "../hooks/windowsSize"
import { useActions, useAppState } from "../overmind"
import Button from "./admin/Button"
import TableSort from "./forms/TableSort"
import LabResult from "./LabResult"
import ReviewForm from "./manual-grading/ReviewForm"
import Search from "./Search"
import SubmissionModal from "./SubmissionModal"
import SubmissionsTable from "./submissions-table/SubmissionsTable"

const Results = ({ review }: { review: boolean }) => {
    const state = useAppState()
    const actions = useActions()
    const courseID = useCourseID()
    const [searchParams, setSearchParams] = useSearchParams()
    const { width } = useWindowSize()
    const [lockedWide, setLockedWide] = useState(false)
    const wideTable = lockedWide || width < ScreenSize.TailwindLg

    const toggleWideTable = useCallback(() => setLockedWide(prev => !prev), [])

    const loaded = state.loadedCourse[courseID.toString()]

    // Always keep latest state/actions/searchParams in a ref for effects
    const latest = useRef({ state, actions, searchParams })
    useEffect(() => {
        latest.current = { state, actions, searchParams }
    }, [state, actions, searchParams])

    // Load the course submissions when the component mounts
    useEffect(() => {
        if (!state.loadedCourse[courseID.toString()]) {
            actions.global.loadCourseSubmissions(courseID)
        }
        return () => {
            actions.global.setGroupView(false)
            actions.review.setAssignmentID(-1n)
            actions.global.setActiveEnrollment(null)
            actions.global.setSelectedSubmission({ submission: null })
        }
    }, [actions, courseID, state.loadedCourse])

    // Select submission from URL if not already selected, after loading is done
    useEffect(() => {
        const { state, actions, searchParams } = latest.current
        if (state.selectedSubmission) {
            return
        }
        const selectedLab = searchParams.get("id")
        if (selectedLab) {
            const submission = state.submissionsForCourse.ByID(convertToBigInt(selectedLab))
            if (submission) {
                const owner = state.submissionsForCourse.OwnerByID(submission.ID)
                if (!owner) {
                    return
                }
                actions.global.setSelectedSubmission({ submission })
                actions.global.updateSubmissionOwner(owner)
                if (submission.reviews.length > 0) {
                    actions.review.setSelectedReview(-1)
                } else {
                    actions.global.getSubmission({ submission, owner, courseID: state.activeCourse })
                }
            }
        }
    }, [loaded])

    const handleSubmissionClick = useCallback((submission: Submission, owner: Enrollment | Group) => {
        actions.global.setSelectedSubmission({ submission })
        if (isMessage(owner, EnrollmentSchema)) {
            actions.global.setActiveEnrollment(clone(EnrollmentSchema, owner))
        }
        actions.global.setSubmissionOwner(owner)
        setSearchParams({ id: submission.ID.toString() })

        if (review) {
            actions.review.setSelectedReview(-1)
        } else {
            actions.global.getSubmission({ submission, owner: state.submissionOwner, courseID: state.activeCourse })
        }
    }, [actions, review, setSearchParams, state.activeCourse, state.submissionOwner])

    const toggleGroupView = useCallback(() => {
        actions.global.setGroupView(!state.groupView)
        actions.review.setAssignmentID(-1n)
    }, [actions, state.groupView])

    const closeModal = useCallback(() => {
        actions.global.setSelectedSubmission({ submission: null })
        setSearchParams({})
    }, [actions, setSearchParams])

    if (!loaded) {
        return (
            <div className="flex justify-center items-center py-12">
                <span className="loading loading-spinner loading-lg" />
            </div>
        )
    }

    let gridCols = "grid-cols-1"
    if (!wideTable) {
        gridCols = state.review.assignmentID >= 0 ? "lg:grid-cols-3" : "lg:grid-cols-2"
    }
    const displayMode = state.groupView ? "Group" : "Student"
    const buttonColor = state.groupView ? Color.BLUE : Color.GREEN

    // showModal is true when wide-table mode is active and a submission has been selected
    const showModal = wideTable && state.selectedSubmission !== null

    return (
        <>
            <div className={`grid grid-cols-1 ${gridCols} gap-6`}>
                <div className="space-y-4">
                    <Search placeholder="Search by name, GitHub login, or group..." className="mb-2">
                        <Button
                            text={`View by ${displayMode}`}
                            color={buttonColor}
                            className="ml-2"
                            onClick={toggleGroupView}
                        />
                        <button
                            className="btn btn-sm btn-ghost border border-base-content/20 ml-2"
                            title={lockedWide ? "Unlock wide view" : "Lock wide view"}
                            onClick={toggleWideTable}
                        >
                            <i className={`fas ${lockedWide ? "fa-compress" : "fa-expand"}`} />
                        </button>
                    </Search>
                    <TableSort />
                    <div className="overflow-x-auto">
                        <SubmissionsTable onSubmissionClick={handleSubmissionClick} review={review} />
                    </div>
                </div>
                {!wideTable && (
                    <div className={state.review.assignmentID >= 0 ? "lg:col-span-2" : ""}>
                        {review ? <ReviewForm /> : <LabResult />}
                    </div>
                )}
            </div>

            {/* Wide-table modal: shown when a submission is selected in wide mode */}
            {showModal && <SubmissionModal review={review} courseID={courseID} onClose={closeModal} />}
        </>
    )
}

export default Results
